import { useCallback } from 'react'
import {
  ControlStatusItem,
  ControlStatuses,
  CreateOrUpdateControlStatusRequest,
  DeleteControlStatusRequest,
} from '@trustero/trustero-api-web/lib/model/control_status_pb'
import { AccountSettingsPromiseClient } from '@trustero/trustero-api-web/lib/accountsettings/accountsettings_grpc_web_pb'
import { NotificationPromiseClient } from '@trustero/trustero-api-web/lib/notification/notification_grpc_web_pb'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { useGrpcRevalidateByMethod } from 'src/components'
import {
  DismissNotificationRequest,
  GetNotificationsRequest,
  GetNotificationsResponse,
  LatestNotificationRequest,
  LatestNotificationResponse,
  NotificationTypeFilter,
  NOTIFICATION_TYPE,
} from '@trustero/trustero-api-web/lib/notification/notification_pb'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import {
  GetDateLimitResponse,
  UpdateDateLimitRequest,
  UpdateValidateImageTimestampsRequest,
  ValidateImageTimestamps,
  GetAccountPromptOverrideSourceResponse,
  UpdateAccountPromptOverrideSourceRequest,
  GetUsesNewAuditScanByDefaultResponse,
  UpdateUsesNewAuditScanByDefaultRequest,
  GetShowOldComplianceDashboardResponse,
} from '@trustero/trustero-api-web/lib/accountsettings/accountsettings_pb'
import log from 'loglevel'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { RiskServicePromiseClient } from '@trustero/trustero-api-web/lib/risk/risk_grpc_web_pb'
import { GetRiskCategoryOptionResponse } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import {
  BoolValue,
  Int32Value,
} from 'google-protobuf/google/protobuf/wrappers_pb'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import { DeleteDepartmentRequest } from '@trustero/trustero-api-web/lib/model/model_pb'
import {
  Departments,
  CreateOrUpdateDepartmentRequest,
} from '@trustero/trustero-api-web/lib/model/department_pb'
import { PromptOverrideSourcesPromiseClient } from '@trustero/trustero-api-web/lib/promptoverridesources/promptoverridesources_grpc_web_pb'
import { LabelServicePromiseClient } from '@trustero/trustero-api-web/lib/label/label_grpc_web_pb'
import {
  LabelRecord,
  Labels,
  LabelID,
  LabelCategoryRecord,
  LabelCategories,
  LabelCategoryID,
} from '@trustero/trustero-api-web/lib/label/label_pb'
import { useInvalidateAuditRunsCache } from '../AuditBot/AuditBot.hooks'

export const useInvalidatePromptOverrideSources = (): (() => Promise<void>) => {
  const mutateFunc = useGrpcRevalidateByMethod()

  return useCallback(async () => {
    await Promise.all([
      mutateFunc(
        PromptOverrideSourcesPromiseClient.prototype.listPromptOverrideSources,
      ),
      mutateFunc(
        AccountSettingsPromiseClient.prototype.getAccountPromptOverrideSource,
      ),
    ])
  }, [mutateFunc])
}

export const useInvalidateAccountSettings = (): (() => Promise<void>) => {
  const mutateFunc = useGrpcRevalidateByMethod()

  return useCallback(async () => {
    await Promise.all([
      mutateFunc(AccountSettingsPromiseClient.prototype.getDateLimit),
      mutateFunc(
        AccountSettingsPromiseClient.prototype.getValidateImageTimestamps,
      ),
      mutateFunc(RiskServicePromiseClient.prototype.getRiskCategoryOption),
      mutateFunc(ModelPromiseClient.prototype.listDepartments),
      mutateFunc(
        PromptOverrideSourcesPromiseClient.prototype.listPromptOverrideSources,
      ),
      mutateFunc(
        AccountSettingsPromiseClient.prototype.getAccountPromptOverrideSource,
      ),
      mutateFunc(
        AccountSettingsPromiseClient.prototype.getUsesNewAuditScanByDefault,
      ),
      mutateFunc(ModelPromiseClient.prototype.listControlStatuses),
      mutateFunc(LabelServicePromiseClient.prototype.list),
      mutateFunc(LabelServicePromiseClient.prototype.listCategories),
    ])
  }, [mutateFunc])
}

export const useInvalidateNotifications = (): (() => Promise<void>) => {
  const mutateFunc = useGrpcRevalidateByMethod()

  return useCallback(async () => {
    await Promise.all([
      mutateFunc(NotificationPromiseClient.prototype.getNotifications),
      mutateFunc(NotificationPromiseClient.prototype.getLatestNotification),
    ])
  }, [mutateFunc])
}

export const useDateLimit = (): GrpcResponse<GetDateLimitResponse> => {
  const { response } = useSwrImmutableGrpc(
    AccountSettingsPromiseClient.prototype.getDateLimit,
    new Empty(),
  )
  return NewGrpcResponse(response)
}

export const useEditDateLimit = (): ((dateLimit: Timestamp) => void) => {
  const client = useAuthorizedGrpcClient(AccountSettingsPromiseClient)
  const mutateAuditRuns = useInvalidateAuditRunsCache()
  const mutateDateLimit = useInvalidateAccountSettings()

  return async (dateLimit: Timestamp) => {
    const request = new UpdateDateLimitRequest().setDateLimit(dateLimit)
    await client.updateDateLimit(request)
    await mutateAuditRuns()
    await mutateDateLimit()
  }
}

export const useValidateImageTimestamp =
  (): GrpcResponse<ValidateImageTimestamps> => {
    const { response } = useSwrImmutableGrpc(
      AccountSettingsPromiseClient.prototype.getValidateImageTimestamps,
      new Empty(),
    )
    return NewGrpcResponse(response)
  }

export const useSetValidateImageTimestamp = (): ((toggle: boolean) => void) => {
  const client = useAuthorizedGrpcClient(AccountSettingsPromiseClient)
  const mutateAuditRuns = useInvalidateAuditRunsCache()
  const mutateValidateImageTimestamp = useInvalidateAccountSettings()

  return async (toggle: boolean) => {
    const request =
      new UpdateValidateImageTimestampsRequest().setValidateImageTimestamps(
        toggle,
      )
    await client.updateValidateImageTimestamps(request)
    await mutateAuditRuns()
    await mutateValidateImageTimestamp()
  }
}

export const useUsesNewAuditScanByDefault =
  (): GrpcResponse<GetUsesNewAuditScanByDefaultResponse> => {
    const { response } = useSwrImmutableGrpc(
      AccountSettingsPromiseClient.prototype.getUsesNewAuditScanByDefault,
      new Empty(),
    )
    return NewGrpcResponse(response)
  }

export const useGetShowOldComplianceDashboard =
  (): GrpcResponse<GetShowOldComplianceDashboardResponse> => {
    const { response } = useSwrImmutableGrpc(
      AccountSettingsPromiseClient.prototype.getShowOldComplianceDashboard,
      new Empty(),
    )
    return NewGrpcResponse(response)
  }

export const useUpdateUsesNewAuditScanByDefault = (): ((
  enable: boolean,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(AccountSettingsPromiseClient)
  const mutate = useInvalidateAccountSettings()

  return async (enable: boolean) => {
    const request =
      new UpdateUsesNewAuditScanByDefaultRequest().setUsesNewAuditScanByDefault(
        enable,
      )
    await client.updateUsesNewAuditScanByDefault(request)
    await mutate()
  }
}

export const useNotifications = (
  message?: string,
  types?: number[],
): GrpcResponse<GetNotificationsResponse> => {
  const request = new GetNotificationsRequest()
  message && request.setMessage(new StringValue().setValue(message))
  types &&
    request.setTypeFilter(new NotificationTypeFilter().setTypesList(types))
  const { response } = useSwrImmutableGrpc(
    NotificationPromiseClient.prototype.getNotifications,
    new GetNotificationsRequest(),
  )
  return NewGrpcResponse(response)
}

export const useLatestNotification = ({
  message,
  type,
}: {
  message: string
  type: NOTIFICATION_TYPE
  shouldFetch?: boolean
}): GrpcResponse<LatestNotificationResponse> => {
  const { response } = useSwrImmutableGrpc(
    NotificationPromiseClient.prototype.getLatestNotification,
    new LatestNotificationRequest().setMessage(message).setType(type),
  )
  return NewGrpcResponse(response)
}

export const useDismissNotification = (): (({
  type,
  message,
}: {
  type: NOTIFICATION_TYPE
  message: string
}) => void) => {
  const client = useAuthorizedGrpcClient(NotificationPromiseClient)
  const mutate = useInvalidateNotifications()

  return async ({ type, message }) => {
    await client.dismissNotification(
      new DismissNotificationRequest().setMessage(message).setType(type),
    )
    await mutate()
  }
}

export const useRiskCategories =
  (): GrpcResponse<GetRiskCategoryOptionResponse> => {
    const { response } = useSwrImmutableGrpc(
      RiskServicePromiseClient.prototype.getRiskCategoryOption,
      new Empty(),
    )
    return NewGrpcResponse(response)
  }

export const useDepartments = (): GrpcResponse<Departments> => {
  const { response } = useSwrImmutableGrpc(
    ModelPromiseClient.prototype.listDepartments,
    new Empty(),
  )
  return NewGrpcResponse(response)
}
export interface DepartmentOption {
  modelId: string
  name: string
  shortName: string
  isCustom?: boolean
  sortOrder?: number
}

export const useUpsertDepartments = (): ((
  departments: DepartmentOption[],
) => void) => {
  const client = useAuthorizedGrpcClient(ModelPromiseClient)

  return async (departments: DepartmentOption[]) => {
    for (const department of departments) {
      const request = new CreateOrUpdateDepartmentRequest()
      request.setModelId(department.modelId)
      request.setName(new StringValue().setValue(department.name))
      request.setShortName(
        new StringValue().setValue(department.shortName || ''),
      )
      request.setIsCustom(new BoolValue().setValue(true))
      request.setIsTemplate(new BoolValue().setValue(false))
      request.setSortOrder(new Int32Value().setValue(department.sortOrder || 0))
      await client.createOrUpdateDepartment(request)
    }
  }
}

export const useDeleteDepartments = (): ((departmentIds: string[]) => void) => {
  const client = useAuthorizedGrpcClient(ModelPromiseClient)

  return async (departmentIds: string[]) => {
    for (const id of departmentIds) {
      const req = new DeleteDepartmentRequest()
      req.setDepartmentId(id)
      await client.deleteDepartment(req)
    }
  }
}

export const useAccountPromptOverrideSource =
  (): GrpcResponse<GetAccountPromptOverrideSourceResponse> => {
    const { response } = useSwrImmutableGrpc(
      AccountSettingsPromiseClient.prototype.getAccountPromptOverrideSource,
      new Empty(),
    )
    return NewGrpcResponse(response)
  }

export const useUpdateAccountPromptOverrideSource = (): ((
  id: string,
) => void) => {
  const client = useAuthorizedGrpcClient(AccountSettingsPromiseClient)
  const mutate = useInvalidatePromptOverrideSources()

  return async (id: string) => {
    try {
      await client.updateAccountPromptOverrideSource(
        new UpdateAccountPromptOverrideSourceRequest().setId(id),
      )
      await mutate()
    } catch (err) {
      log.error('Error updating account prompt override source', err)
      showInfoToast('Error updating account prompt override source')
    }
  }
}

export const useControlStatuses = (): GrpcResponse<ControlStatuses> => {
  const { response } = useSwrImmutableGrpc(
    ModelPromiseClient.prototype.listControlStatuses,
    new Empty(),
  )
  return NewGrpcResponse(response)
}

export interface ControlStatusOption {
  id?: string
  tempId?: string
  name: string
  color?: string
  borderColor?: string
  order?: number
}

export const useUpsertControlStatuses = (): ((
  statuses: ControlStatusOption[],
) => void) => {
  const client = useAuthorizedGrpcClient(ModelPromiseClient)

  return async (statuses: ControlStatusOption[]) => {
    const request = new CreateOrUpdateControlStatusRequest()
    const statusesList: ControlStatusItem[] = []

    for (const status of statuses) {
      const item = new ControlStatusItem()

      item.setId(status?.id || '')
      item.setName(status.name)
      item.setColor(status?.color || '')
      item.setBorderColor(status?.borderColor || '')
      item.setOrder(status?.order || 0)

      statusesList.push(item)
    }

    request.setStatusesList(statusesList)
    await client.createOrUpdateControlStatus(request)
  }
}

export const useDeleteControlStatuses = (): ((statusIds: string[]) => void) => {
  const client = useAuthorizedGrpcClient(ModelPromiseClient)

  return async (statusIds: string[]) => {
    for (const id of statusIds) {
      const req = new DeleteControlStatusRequest()
      req.setControlStatusId(id)
      await client.deleteControlStatus(req)
    }
  }
}

export const useLabels = (): GrpcResponse<Labels> => {
  const { response } = useSwrImmutableGrpc(
    LabelServicePromiseClient.prototype.list,
    new Empty(),
  )
  return NewGrpcResponse(response)
}

export interface LabelOption {
  id?: string
  tempId?: string
  name: string
  sortOrder?: number
  categoryId?: string
}

export const useUpsertLabels = (): ((
  labels: LabelOption[],
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(LabelServicePromiseClient)

  return async (labels: LabelOption[]) => {
    for (const label of labels) {
      const request = new LabelRecord()
      request.setId(label?.id || '')
      request.setName(label.name)
      request.setSortOrder(label?.sortOrder || 0)
      request.setCategoryId(label?.categoryId || '')

      if (label.id) {
        await client.update(request)
      } else {
        await client.create(request)
      }
    }
  }
}

export const useDeleteLabels = (): ((labelIds: string[]) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(LabelServicePromiseClient)

  return async (labelIds: string[]) => {
    for (const id of labelIds) {
      const req = new LabelID()
      req.setId(id)
      await client.delete(req)
    }
  }
}

export const useLabelCategories = (): GrpcResponse<LabelCategories> => {
  const { response } = useSwrImmutableGrpc(
    LabelServicePromiseClient.prototype.listCategories,
    new Empty(),
  )
  return NewGrpcResponse(response)
}

export interface LabelCategoryOption {
  id?: string
  tempId?: string
  name: string
  sortOrder?: number
}

export const useUpsertLabelCategories = (): ((
  categories: LabelCategoryOption[],
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(LabelServicePromiseClient)

  return async (categories: LabelCategoryOption[]) => {
    for (const cat of categories) {
      const request = new LabelCategoryRecord()
      request.setId(cat?.id || '')
      request.setName(cat.name)
      request.setSortOrder(cat?.sortOrder || 0)

      if (cat.id) {
        await client.updateCategory(request)
      } else {
        await client.createCategory(request)
      }
    }
  }
}

export const useDeleteLabelCategories = (): ((
  categoryIds: string[],
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(LabelServicePromiseClient)

  return async (categoryIds: string[]) => {
    for (const id of categoryIds) {
      const req = new LabelCategoryID()
      req.setId(id)
      await client.deleteCategory(req)
    }
  }
}
